/**
 * ScheduleReportDrawer — create-mode drawer for scheduling a report.
 *
 * Captures: description, frequency, day selection, send time, attachments, emails.
 * Filters and time range are read-only, inherited from the parent page state.
 *
 * Designed to be reusable across all report pages by passing reportTypeId.
 *
 * Props:
 *   open, onClose, onSave, saving,
 *   reportTypeId, rangeId, rangeName,
 *   reportFilters, filterOptions, campusId, frequencies, tzOffset
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { calculateSendTime } from '../../../../libs.jsx';
import '../../scheduled/scheduled-reports-page.css';

const WEEKLY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHLY_DAYS = [...Array.from({ length: 31 }, (_, i) => i + 1), 0];

function timeInputToSeconds(timeStr) {
    if (!timeStr) return 32400;
    const [h, m] = timeStr.split(':').map(Number);
    return (h * 3600) + (m * 60);
}

function secondsToTimeStr(sec) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Convert the V2 reportFilters (arrays) into the report_filter_terms JSON
 * format the backend expects: { area_filter: { values: "1,2", labels: "Area A, Area B" }, ... }
 */
function buildFilterTerms(reportFilters, filterOptions) {
    const result = {};
    const keyMap = {
        areas: { dbKey: 'area_filter', optionsKey: 'areas', idKey: 'area_id', nameKey: 'area_name' },
        areaGroups: { dbKey: 'area_group_filter', optionsKey: 'areaGroups', idKey: 'area_group_id', nameKey: 'area_group_name' },
        areaTypes: { dbKey: 'area_type_filter', optionsKey: 'areaTypes', idKey: 'area_type_id', nameKey: 'area_type_name' },
        residents: { dbKey: 'resident_filter', optionsKey: 'residents', idKey: 'resident_id', nameKey: 'resident_name' },
        residentGroups: { dbKey: 'resident_group_filter', optionsKey: 'residentGroups', idKey: 'resident_group_id', nameKey: 'resident_group_name' },
        users: { dbKey: 'user_filter', optionsKey: 'users', idKey: 'user_id', nameKey: 'display_name' },
        userGroups: { dbKey: 'user_group_filter', optionsKey: 'userGroups', idKey: 'group_id', nameKey: 'group_name' },
        deviceCategories: { dbKey: 'device_category_filter', optionsKey: 'deviceCategories', idKey: 'device_category_id', nameKey: 'device_category_short_name' },
        devices: { dbKey: 'device_filter', optionsKey: 'devices', idKey: 'device_id', nameKey: 'device_name' },
        eventTypes: { dbKey: 'event_type_filter', optionsKey: 'eventTypes', idKey: 'event_type_id', nameKey: 'event_type_name' },
        reasons: { dbKey: 'reason_filter', optionsKey: 'reasonCodes', idKey: 'reason_code_id', nameKey: 'reason_code_description' },
    };

    Object.entries(keyMap).forEach(([stateKey, { dbKey, optionsKey, idKey, nameKey }]) => {
        const arr = reportFilters?.[stateKey];
        if (Array.isArray(arr) && arr.length > 0) {
            const options = filterOptions?.[optionsKey] || [];
            const labels = arr.map((id) => {
                const opt = options.find((o) => String(o[idKey]) === String(id));
                return opt ? opt[nameKey] : id;
            });
            result[dbKey] = { values: arr.join(','), labels: labels.join(', ') };
        } else {
            result[dbKey] = { values: '', labels: '' };
        }
    });

    // Response time
    const rt = reportFilters?.responseTime;
    if (rt?.enabled) {
        const opLabel = rt.operator === 'lt' ? 'less than' : rt.operator === 'gt' ? 'greater than' : 'equal to';
        result.resp_filter_items = { values: [true, rt.operator, rt.value], labels: `Response Time ${opLabel} ${rt.value} min` };
    } else {
        result.resp_filter_items = { values: [false, 'lt', 60], labels: '' };
    }

    return result;
}

/** Build read-only filter chips from reportFilters */
function buildFilterChips(reportFilters) {
    const chips = [];
    const labelMap = {
        areas: 'Areas', areaGroups: 'Area Groups', areaTypes: 'Area Types',
        residents: 'Residents', residentGroups: 'Resident Groups',
        users: 'Users', userGroups: 'User Groups',
        deviceCategories: 'Device Categories', devices: 'Devices',
        eventTypes: 'Event Types', reasons: 'Reasons',
    };
    Object.entries(reportFilters || {}).forEach(([key, vals]) => {
        if (key === 'responseTime') {
            if (vals?.enabled) chips.push({ key, label: `Response Time: ${vals.operator} ${vals.value}min` });
        } else if (Array.isArray(vals) && vals.length > 0) {
            chips.push({ key, label: `${labelMap[key] || key}: ${vals.length}` });
        }
    });
    return chips;
}

const ScheduleReportDrawer = ({
    open, onClose, onSave, saving,
    reportTypeId, rangeId, rangeName,
    reportFilters, filterOptions, campusId, frequencies, tzOffset,
}) => {
    const [description, setDescription] = useState('');
    const [frequencyId, setFrequencyId] = useState(1);
    const [selectedDays, setSelectedDays] = useState(new Set());
    const [sendTime, setSendTime] = useState('09:00');
    const [pdfSummary, setPdfSummary] = useState(true);
    const [pdfDetails, setPdfDetails] = useState(false);
    const [csvDetails, setCsvDetails] = useState(false);
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [userSearch, setUserSearch] = useState('');

    const isExecutive = reportTypeId === 5;
    const filterChips = buildFilterChips(reportFilters);

    // Users list from filterOptions (same data as Report Settings drawer)
    const userOptions = useMemo(() => filterOptions?.users || [], [filterOptions]);
    const filteredUserOptions = useMemo(() => {
        if (!userSearch) return userOptions;
        const term = userSearch.toLowerCase();
        return userOptions.filter((u) =>
            (u.display_name || '').toLowerCase().includes(term) ||
            (u.email || '').toLowerCase().includes(term)
        );
    }, [userOptions, userSearch]);

    // Reset form when drawer opens
    useEffect(() => {
        if (open) {
            setDescription('');
            setFrequencyId(1);
            setSelectedDays(new Set());
            setSendTime('09:00');
            setPdfSummary(true);
            setPdfDetails(false);
            setCsvDetails(false);
            setEmails([]);
            setNewEmail('');
            setEmailError('');
            setSelectedUserIds(new Set());
            setUserSearch('');
        }
    }, [open]);

    // Focus first input
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => document.getElementById('srd-description')?.focus(), 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Escape key
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        if (open) document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [open, onClose]);

    const toggleDay = useCallback((day) => {
        setSelectedDays((prev) => {
            const next = new Set(prev);
            const val = String(day);
            if (next.has(val)) next.delete(val); else next.add(val);
            return next;
        });
    }, []);

    const handleToggleUser = useCallback((userId) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            const id = String(userId);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const handleSelectAllUsers = useCallback(() => {
        setSelectedUserIds(new Set(filteredUserOptions.filter((u) => Boolean(u.email)).map((u) => String(u.user_id))));
    }, [filteredUserOptions]);

    const handleClearUsers = useCallback(() => {
        setSelectedUserIds(new Set());
    }, []);

    const handleAddEmail = useCallback(() => {
        const email = newEmail.trim().toLowerCase();
        const pattern = /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/;
        if (!email) return;
        if (!pattern.test(email)) { setEmailError('Invalid email format'); return; }
        if (emails.includes(email)) { setEmailError('Email already added'); return; }
        setEmails((prev) => [...prev, email]);
        setNewEmail('');
        setEmailError('');
    }, [newEmail, emails]);

    const handleRemoveEmail = useCallback((email) => {
        setEmails((prev) => prev.filter((e) => e !== email));
    }, []);

    const handleSubmit = useCallback(() => {
        const freqRate = frequencyId === 1 ? ['1'] : [...selectedDays];
        const sendTimeSec = timeInputToSeconds(sendTime);
        const nextSendTime = calculateSendTime(
            frequencyId, freqRate, sendTime, parseInt(tzOffset || 0, 10)
        );
        const filterTerms = buildFilterTerms(reportFilters, filterOptions);

        onSave({
            campus_id: campusId,
            description,
            issue_freq_id: frequencyId,
            window_frequency_reference: freqRate.join(','),
            send_report_time: sendTimeSec,
            enabled: 1,
            report_type_id: reportTypeId,
            report_interval_id: rangeId,
            report_filter_terms: filterTerms,
            report_receiver: emails.join(','),
            report_user_ids: [...selectedUserIds].join(','),
            next_send_time: nextSendTime,
            pdf_summary: pdfSummary ? 1 : 0,
            pdf_details: pdfDetails ? 1 : 0,
            csv_details: csvDetails ? 1 : 0,
        });
    }, [campusId, description, frequencyId, selectedDays, sendTime, reportTypeId, rangeId, reportFilters, filterOptions, emails, selectedUserIds, pdfSummary, pdfDetails, csvDetails, tzOffset, onSave]);

    const canSubmit = description.trim() && (emails.length > 0 || selectedUserIds.size > 0);

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer dp-drawer--lg${open ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-label="Schedule Report">
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Schedule Report</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'\u2715'}</button>
                </div>

                <div className="dp-drawer__body" style={{ overflowY: 'auto', flex: 1 }}>

                    {/* ── Schedule Details ── */}
                    <div className="sr-form-card">
                        <h3 className="sr-form-card__title">Schedule Details</h3>

                        {/* Description */}
                        <div className="sr-form-section">
                            <label htmlFor="srd-description" className="sr-form-section__label">Description</label>
                            <input
                                id="srd-description"
                                type="text"
                                className="sr-email-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="e.g. Daily Alert Summary"
                                required
                            />
                        </div>

                        {/* Time Range (read-only) */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Report Range</span>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                <i className="fe fe-clock" style={{ marginRight: '4px' }} />{rangeName}
                            </span>
                        </div>

                        {/* Frequency */}
                        <div className="sr-form-section">
                            <label htmlFor="srd-frequency" className="sr-form-section__label">Delivery Rate</label>
                            <select
                                id="srd-frequency"
                                className="sr-time-input"
                                value={frequencyId}
                                onChange={(e) => { setFrequencyId(parseInt(e.target.value, 10)); setSelectedDays(new Set()); }}
                                style={{ width: '100%' }}
                            >
                                {(frequencies || []).map((f) => (
                                    <option key={f.window_frequency_id} value={f.window_frequency_id}>{f.frequency_name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Weekly day picker */}
                        {frequencyId === 2 ? (
                            <div className="sr-form-section">
                                <span className="sr-form-section__label">Days of Week</span>
                                <div className="sr-day-picker">
                                    {WEEKLY_DAYS.map((day, idx) => (
                                        <button key={idx} type="button" className={`sr-day-chip${selectedDays.has(String(idx)) ? ' is-selected' : ''}`} onClick={() => toggleDay(idx)}>{day}</button>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {/* Monthly day picker */}
                        {frequencyId === 3 ? (
                            <div className="sr-form-section">
                                <span className="sr-form-section__label">Days of Month</span>
                                <div className="sr-day-picker">
                                    {MONTHLY_DAYS.map((day) => (
                                        <button key={day} type="button" className={`sr-day-chip${selectedDays.has(String(day)) ? ' is-selected' : ''}`} onClick={() => toggleDay(day)}>{day === 0 ? 'Last' : day}</button>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {/* Send Time */}
                        <div className="sr-form-section">
                            <label htmlFor="srd-sendtime" className="sr-form-section__label">Time of Day</label>
                            <input id="srd-sendtime" type="time" className="sr-time-input" value={sendTime} onChange={(e) => setSendTime(e.target.value)} step={300} />
                        </div>
                    </div>

                    {/* ── Attachments & Filters ── */}
                    <div className="sr-form-card">
                        <h3 className="sr-form-card__title">Attachments &amp; Filters</h3>

                        {/* Attachments */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Attachments</span>
                            <div className="sr-attachments">
                                <label className="sr-attachment-item">
                                    <input type="checkbox" checked={pdfSummary} onChange={(e) => setPdfSummary(e.target.checked)} />
                                    PDF Attachment (Summary)
                                </label>
                                <label className="sr-attachment-item">
                                    <input type="checkbox" checked={pdfDetails} onChange={(e) => setPdfDetails(e.target.checked)} />
                                    PDF Attachment (Details)
                                </label>
                                {!isExecutive ? (
                                    <label className="sr-attachment-item">
                                        <input type="checkbox" checked={csvDetails} onChange={(e) => setCsvDetails(e.target.checked)} />
                                        CSV Attachment (Details)
                                    </label>
                                ) : null}
                            </div>
                        </div>

                        {/* Applied Filters (read-only) */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Applied Filters</span>
                            <span className="sr-form-section__sublabel">Filters are set via Report Settings</span>
                            <div className="sr-filters-summary">
                                {filterChips.length === 0 ? (
                                    <span className="sr-no-filters">No filters applied</span>
                                ) : null}
                                {filterChips.map((chip) => (
                                    <span key={chip.key} className="sr-filter-tag">
                                        <span className="sr-filter-tag__label">{chip.label}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ── Recipients ── */}
                    <div className="sr-form-card">
                        <h3 className="sr-form-card__title">Recipients</h3>

                        {/* User Recipients */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">User Recipients</span>
                            <span className="sr-form-section__sublabel">
                                Select users whose email addresses will receive this report
                                {selectedUserIds.size > 0 ? ` (${selectedUserIds.size} selected)` : ''}
                            </span>
                            <div className="ar-filter-section" style={{ marginTop: '8px' }}>
                                <div className="ar-filter-section__header">
                                    <span className="ar-filter-section__label">Users</span>
                                    <div className="ar-filter-section__actions">
                                        <button className="ar-filter-section__btn" onClick={handleSelectAllUsers} type="button">Select All</button>
                                        <button className="ar-filter-section__btn" onClick={handleClearUsers} type="button">Clear</button>
                                    </div>
                                </div>
                                <input
                                    className="ar-filter-section__search"
                                    type="text"
                                    placeholder="Search users..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                />
                                <div className="ar-filter-list">
                                    {filteredUserOptions.length === 0 ? (
                                        <p className="ar-filter-list__empty">No users found.</p>
                                    ) : null}
                                    {filteredUserOptions.map((user) => {
                                        const id = String(user.user_id);
                                        const checked = selectedUserIds.has(id);
                                        const hasEmail = Boolean(user.email);
                                        return (
                                            <label key={id} className={`ar-filter-list__item ar-filter-list__item--has-subtext${!hasEmail ? ' ar-filter-list__item--disabled' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => handleToggleUser(id)}
                                                    disabled={!hasEmail}
                                                />
                                                <span className="ar-filter-list__item-content">
                                                    <span className="ar-filter-list__item-name">{user.display_name || '(unknown)'}</span>
                                                    <span className="ar-filter-list__item-subtext">
                                                        {hasEmail ? user.email : 'No email on file'}
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Email Recipients */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Email Recipients</span>
                            <div className="sr-email-list">
                                {emails.map((email) => (
                                    <span key={email} className="sr-email-chip">
                                        {email}
                                        <button type="button" className="sr-email-chip__remove" onClick={() => handleRemoveEmail(email)} aria-label={`Remove ${email}`}>{'\u2715'}</button>
                                    </span>
                                ))}
                            </div>
                            <div className="sr-email-add-row">
                                <input
                                    type="email"
                                    className="sr-email-input"
                                    placeholder="name@example.com"
                                    value={newEmail}
                                    onChange={(e) => { setNewEmail(e.target.value); setEmailError(''); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); } }}
                                />
                                <button type="button" className="rp-btn rp-btn--secondary" onClick={handleAddEmail}>Add</button>
                            </div>
                            {emailError ? (
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: '4px', display: 'block' }}>{emailError}</span>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving} type="button">Cancel</button>
                    <button className="dp-btn dp-btn--primary" onClick={handleSubmit} disabled={saving || !canSubmit} type="button">
                        {saving ? 'Creating\u2026' : 'Create Schedule'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ScheduleReportDrawer;
